import { useEffect, useRef } from 'react';
import { Platform, Pressable, Text as RNText, TextInput, View } from 'react-native';
import Sortable from 'react-native-sortables';

import { blockedInputProps } from '@/components/domain/fieldPresence/blockedInputProps';
import { blockedPressableProps } from '@/components/domain/fieldPresence/blockedPressableProps';
import { FieldClaimFrame } from '@/components/domain/fieldPresence/FieldClaimFrame';
import { GatedAddAffordance } from '@/components/domain/fieldPresence/GatedAddAffordance';
import { type ClaimableField } from '@/components/domain/fieldPresence/useClaimableField';
import { cn } from '@/components/ui/cn';
import { Text } from '@/components/ui/Text';

/** One editable field of the step: the draft value plus its edit callbacks. */
export interface StepEditorField {
  value: string;
  onChange: (text: string) => void;
  onFocus?: () => void;
  /** Commit the field (persist on blur). */
  onBlur?: () => void;
  /** The field's presence wiring (claims, gating, input ref). */
  presence?: ClaimableField;
}

/** The step's reference note; `value: null` means none yet (renders the add affordance). */
export interface StepEditorNote {
  value: string | null;
  onChange?: (text: string) => void;
  onFocus?: () => void;
  /** Commit the note (persist on blur). */
  onBlur?: () => void;
  onAdd?: () => void;
  onRemove?: () => void;
  /** The note's presence wiring (claims, gating, input ref). */
  presence?: ClaimableField;
}

export interface StepEditorRowProps {
  /** The step's stable id — anchors the handle's DOM id for keyboard focus restore. */
  id: string;
  position: number;
  label: StepEditorField;
  referenceNote?: StepEditorNote;
  onRemove: () => void;
  /** Move this step one slot up/down (keyboard reorder). */
  onMoveUp: () => void;
  onMoveDown: () => void;
  /** Whether a move in each direction is possible (false at the list ends). */
  canMoveUp: boolean;
  canMoveDown: boolean;
  /** Dim the remove affordance while any field on this step is claimed. */
  removeClaimed?: boolean;
  className?: string;
}

/** An editable template step: drag handle, number, label, remove, reference note. */
export function StepEditorRow({
  id,
  position,
  label,
  referenceNote,
  onRemove,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  removeClaimed = false,
  className,
}: StepEditorRowProps) {
  const labelBlocked = label.presence?.blocked ?? false;
  const noteBlocked = referenceNote?.presence?.blocked ?? false;
  // Keyboard reorder: the drag handle is a focusable control; Arrow Up/Down move
  // the step one slot. react-native-sortables has no keyboard API, so we handle
  // keys ourselves and let the parent's reactive `ORDER BY position` query
  // re-render — the same path a completed drag takes. Web-only (native reorders
  // by drag); we attach to the DOM node directly rather than via an onKeyDown
  // prop so no press responder lands inside Sortable.Handle to fight the gesture.
  const handleRef = useRef<View>(null);
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const node = handleRef.current as unknown as HTMLElement | null;
    if (!node) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp' && canMoveUp) {
        e.preventDefault();
        onMoveUp();
      } else if (e.key === 'ArrowDown' && canMoveDown) {
        e.preventDefault();
        onMoveDown();
      }
    };
    node.addEventListener('keydown', onKeyDown);
    return () => node.removeEventListener('keydown', onKeyDown);
  }, [canMoveUp, canMoveDown, onMoveUp, onMoveDown]);

  return (
    <View className={cn('gap-2 py-2', className)}>
      <View className="flex-row items-center gap-2">
        <Sortable.Handle>
          <View
            ref={handleRef}
            focusable
            nativeID={`step-handle-${id}`}
            accessibilityRole="button"
            accessibilityLabel={
              Platform.OS === 'web'
                ? `Reorder "${label.value || 'step'}", press up or down arrow to move`
                : `Reorder "${label.value || 'step'}"`
            }
          >
            <RNText className="px-1 text-lg text-faint">≡</RNText>
          </View>
        </Sortable.Handle>
        <Text variant="accentSmall" className="w-6 text-faint">
          {String(position).padStart(2, '0')}
        </Text>
        <FieldClaimFrame field={label.presence} className="flex-1">
          <TextInput
            ref={label.presence?.inputRef}
            value={label.value}
            onChangeText={label.onChange}
            onFocus={label.onFocus}
            onBlur={label.onBlur}
            {...blockedInputProps(labelBlocked)}
            placeholder="Step label"
            placeholderTextColorClassName="accent-faint"
            className="py-1 font-sans-medium text-base text-foreground"
          />
        </FieldClaimFrame>
        <Pressable
          onPress={onRemove}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Remove step"
          className={cn('px-1', removeClaimed && 'opacity-40')}
        >
          <RNText className="text-base text-faint">✕</RNText>
        </Pressable>
      </View>

      {referenceNote?.value != null ? (
        <FieldClaimFrame field={referenceNote.presence} className="ml-9">
          <View className="gap-1 rounded-lg bg-well px-3 py-2">
            <View className="flex-row items-center justify-between">
              <Text variant="accent" className="text-[10px]">
                Reference note
              </Text>
              {referenceNote.onRemove ? (
                <Pressable
                  onPress={referenceNote.onRemove}
                  hitSlop={6}
                  {...blockedPressableProps(noteBlocked)}
                >
                  <RNText className="font-sans text-xs text-faint">Remove</RNText>
                </Pressable>
              ) : null}
            </View>
            <TextInput
              ref={referenceNote.presence?.inputRef}
              value={referenceNote.value}
              onChangeText={referenceNote.onChange}
              onFocus={referenceNote.onFocus}
              onBlur={referenceNote.onBlur}
              {...blockedInputProps(noteBlocked)}
              placeholder="Guidance shown during every run…"
              placeholderTextColorClassName="accent-faint"
              multiline
              className="font-sans text-sm text-foreground-secondary"
            />
          </View>
        </FieldClaimFrame>
      ) : referenceNote?.onAdd ? (
        <GatedAddAffordance
          label="+ Add reference note"
          field={referenceNote.presence}
          onPress={referenceNote.onAdd}
          className="ml-9"
        />
      ) : null}
    </View>
  );
}
